import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import PremiumLock from '../components/ui/PremiumLock';
import DateRangeSelector from '../components/ui/DateRangeSelector';
import useDashboardStore from '../store/dashboardStore';
import api from '../api/axios';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { DollarSign, TrendingUp, Receipt, Percent, Calendar, Award, Activity } from 'lucide-react';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';

// Colores EstetikFlow
const COLORS = {
  teal: '#2A9D8F',
  coral: '#E76F51',
  nude: '#E9C46A',
  tealLight: '#3DB5A6',
  tealDark: '#227f74'
};

// Colores para el PieChart de estados
const PIE_COLORS = {
  completada: COLORS.teal,
  cancelada: COLORS.coral,
  reservada: COLORS.nude,
  confirmada: '#60A5FA'
};

// Formatear moneda CLP
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(value);
};

// Componente KPI Card
const KPICard = ({ icon: Icon, title, value, subtitle, color = 'teal' }) => {
  const colorClasses = {
    teal: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    coral: 'bg-[#E76F51]/10 text-[#E76F51]',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

// Tooltip personalizado para el gráfico de área
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
        <p className="font-semibold text-gray-800">{label}</p>
        <p className="text-[#2A9D8F] font-medium">
          {formatCurrency(payload[0].value)}
        </p>
        {payload[0].payload.appointments && (
          <p className="text-xs text-gray-500">
            {payload[0].payload.appointments} cita{payload[0].payload.appointments !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    );
  }
  return null;
};

// Tooltip para el PieChart
const PieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0];
    // Calcular porcentaje si no está disponible
    const percent = item.percent !== undefined 
      ? (item.percent * 100).toFixed(0)
      : null;
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
        <p className="font-semibold text-gray-800">{item.name}</p>
        <p className="text-[#2A9D8F] font-medium">
          {item.value} citas{percent ? ` (${percent}%)` : ''}
        </p>
      </div>
    );
  }
  return null;
};

const RentabilidadPage = () => {
  const { canAccessFinance, planName } = usePermissions();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Store global de fechas - compartido con DashboardPage
  const { selectedDate, viewMode, getDateRange } = useDashboardStore();

  // Fetch data basado en el store global
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { startDate, endDate } = getDateRange();
      
      const response = await api.get('/dashboard/financial-summary/', {
        params: {
          start_date: startDate,
          end_date: endDate,
          filter_type: viewMode
        }
      });
      
      setData(response.data);
    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError('Error al cargar los datos financieros');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, viewMode]);

  // Effect to refetch data when filter changes
  useEffect(() => {
    if (canAccessFinance) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [canAccessFinance, selectedDate, viewMode, fetchData]);

  // Obtener label del período
  const getPeriodLabel = () => {
    if (viewMode === 'daily') {
      return format(selectedDate, "EEEE d 'de' MMMM yyyy", { locale: es });
    } else if (viewMode === 'monthly') {
      return format(selectedDate, "MMMM 'de' yyyy", { locale: es });
    } else {
      return format(selectedDate, 'yyyy');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Rentabilidad y Finanzas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Análisis detallado de tus ingresos y rendimiento
          </p>
        </div>
      </div>

      {/* Selector de Fecha - Ahora sin props, conectado al store global */}
      {canAccessFinance && (
        <DateRangeSelector showLabel={true} />
      )}

      {/* Main Content with PremiumLock */}
      <PremiumLock 
        access={canAccessFinance} 
        featureName="Dashboard Financiero"
      >
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A9D8F]"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        ) : data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPICard
                icon={DollarSign}
                title="Ingresos Totales"
                value={formatCurrency(data.kpis?.total_revenue || 0)}
                subtitle={getPeriodLabel()}
                color="teal"
              />
              <KPICard
                icon={Receipt}
                title="Ticket Promedio"
                value={formatCurrency(data.kpis?.avg_ticket || 0)}
                subtitle="Por cita completada"
                color="blue"
              />
              <KPICard
                icon={TrendingUp}
                title="Ganancia Neta"
                value={formatCurrency(data.kpis?.net_profit || 0)}
                subtitle="Ingresos - Costos"
                color="green"
              />
              <KPICard
                icon={Percent}
                title="Margen de Ganancia"
                value={`${data.kpis?.profit_margin || 0}%`}
                subtitle={`${data.kpis?.total_appointments || 0} citas completadas`}
                color="coral"
              />
            </div>

            {/* Charts Row - Principal */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Area Chart - Evolución de Ingresos */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Evolución de Ingresos
                </h2>
                <div className="h-72 w-full" style={{ minHeight: 288 }}>
                  {data.revenue_by_day?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={data.revenue_by_day}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.teal} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={COLORS.teal} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="display_date" 
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                        />
                        <YAxis 
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                          axisLine={{ stroke: '#E5E7EB' }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="amount" 
                          stroke={COLORS.teal}
                          strokeWidth={3}
                          fillOpacity={1} 
                          fill="url(#colorRevenue)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Sin datos en este período</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pie Chart - Estado de Citas */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#2A9D8F]" />
                  Estado de Citas
                </h2>
                <div className="h-72 w-full" style={{ minHeight: 288 }}>
                  {data.appointments_by_status?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.appointments_by_status}
                          cx="50%"
                          cy="45%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="count"
                          nameKey="status"
                        >
                          {data.appointments_by_status.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={PIE_COLORS[entry.status_code] || COLORS.tealLight} 
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Sin citas en este período</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Second Row - Top Tratamientos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Tratamientos */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#2A9D8F]" />
                  Top Tratamientos Rentables
                </h2>
                <div className="space-y-4">
                  {data.top_treatments?.length > 0 ? (
                    data.top_treatments.map((treatment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            index === 0 ? 'bg-[#2A9D8F]' : 
                            index === 1 ? 'bg-[#3DB5A6]' : 
                            index === 2 ? 'bg-[#E9C46A]' : 'bg-gray-400'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{treatment.name}</p>
                            <p className="text-xs text-gray-500">{treatment.count} citas</p>
                          </div>
                        </div>
                        <p className="font-semibold text-[#2A9D8F]">
                          {formatCurrency(treatment.revenue)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-400 py-8">
                      <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Sin tratamientos en este período</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Resumen de Cancelaciones (pérdida potencial) */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#E76F51]" />
                  Análisis de Pérdidas
                </h2>
                <div className="space-y-4">
                  {/* Citas canceladas */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Citas Canceladas</p>
                        <p className="text-2xl font-bold text-[#E76F51]">
                          {data.kpis?.cancelled_appointments || 0}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Ingreso Potencial Perdido</p>
                        <p className="text-lg font-semibold text-[#E76F51]">
                          {formatCurrency(data.kpis?.lost_revenue || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tasa de cumplimiento */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Tasa de Cumplimiento</p>
                        <p className="text-2xl font-bold text-green-600">
                          {data.kpis?.completion_rate || 0}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Citas Completadas</p>
                        <p className="text-lg font-semibold text-green-600">
                          {data.kpis?.total_appointments || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Footer */}
            <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Período analizado</p>
                  <p className="font-semibold text-gray-800 capitalize">
                    {getPeriodLabel()}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-full bg-[#2A9D8F]"></span>
                    Completadas
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded-full bg-[#E76F51]"></span>
                    Canceladas
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </PremiumLock>
    </div>
  );
};

export default RentabilidadPage;
